import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Send, X } from "lucide-react";
import apiClient from "@/lib/api-client";

/**
 * CHAT DE SOPORTE integrado (12-sep-2026, petición de Luis): tercera vía del
 * botón de ayuda, la más rápida — el hilo cae directo en el buzón de
 * /admin/soporte del superadmin. Contesta Carmen (misma IA que el soporte por
 * WhatsApp) y el equipo toma el control cuando hace falta.
 *
 * El POST devuelve el hilo con la respuesta de la IA ya incluida; el polling
 * de 5s solo existe para recoger las respuestas del equipo humano.
 */

interface SupportChatMessage {
  id: string;
  direction: "in" | "out";
  senderType: "customer" | "bot" | "staff";
  body: string;
  createdAt: string;
}

interface SupportChatThread {
  mode: string | null;
  messages: SupportChatMessage[];
}

const POLL_MS = 5000;

/** Negritas de WhatsApp (*texto*) → <strong>, que es como las escribe Carmen. */
function renderBody(body: string): ReactNode[] {
  return body.split(/(\*[^*\n]+\*)/g).map((part, i) =>
    part.startsWith("*") && part.endsWith("*") && part.length > 2 ? (
      <strong key={i}>{part.slice(1, -1)}</strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function senderLabel(message: SupportChatMessage): string | null {
  if (message.direction === "in") return null;
  return message.senderType === "staff" ? "Equipo ReservaManager" : "Carmen · IA";
}

export default function SupportChatPanel({
  onClose,
  side = "right",
}: {
  onClose: () => void;
  /** Esquina desde la que se abre (sigue a la bola de ayuda). */
  side?: "left" | "right";
}) {
  const [thread, setThread] = useState<SupportChatThread>({ mode: null, messages: [] });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [text, setText] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ success: true; data: SupportChatThread }>(
        "/support-chat",
      );
      setThread(data.data);
    } catch {
      // Silencioso: el polling reintenta solo.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Al fondo con cada mensaje nuevo.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [thread.messages.length, loading]);

  const handleSend = useCallback(async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setSendError(false);
    setText("");
    // Eco optimista mientras la IA responde (el POST devuelve el hilo real).
    setThread((prev) => ({
      ...prev,
      messages: [
        ...prev.messages,
        {
          id: `optimistic-${Date.now()}`,
          direction: "in",
          senderType: "customer",
          body,
          createdAt: new Date().toISOString(),
        },
      ],
    }));
    try {
      const { data } = await apiClient.post<{ success: true; data: SupportChatThread }>(
        "/support-chat/messages",
        { body },
      );
      setThread(data.data);
    } catch {
      setSendError(true);
      setText(body); // que no pierda lo escrito
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [text, sending]);

  return (
    <div
      className={`absolute bottom-16 w-[min(24rem,calc(100vw-2.5rem))] h-[min(30rem,70vh)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden ${side === "left" ? "left-0" : "right-0"}`}
    >
      {/* Cabecera */}
      <div className="flex-shrink-0 bg-primary-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold">Soporte ReservaManager</p>
          <p className="text-xs text-primary-100 truncate">
            Carmen, asistente de IA · el equipo entra cuando hace falta
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-primary-500 transition-colors"
          aria-label="Cerrar chat"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Mensajes */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3 space-y-2 bg-gray-50">
        {loading ? (
          <p className="text-center text-sm text-gray-400 py-8">Cargando...</p>
        ) : thread.messages.length === 0 ? (
          <div className="text-center py-8 px-4">
            <p className="text-sm text-gray-600">
              ¡Hola! 👋 Soy <strong>Carmen</strong>, asistente IA de soporte.
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Cuéntame en qué te ayudo y te respondo al momento.
            </p>
          </div>
        ) : (
          thread.messages.map((message) => {
            const own = message.direction === "in";
            const label = senderLabel(message);
            return (
              <div key={message.id} className={`flex ${own ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    own
                      ? "bg-primary-600 text-white rounded-br-md"
                      : "bg-white border border-gray-200 text-gray-800 rounded-bl-md"
                  }`}
                >
                  {label && (
                    <p className="text-[11px] font-semibold text-primary-600 mb-0.5">{label}</p>
                  )}
                  {renderBody(message.body)}
                </div>
              </div>
            );
          })
        )}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-3 py-2 text-sm text-gray-400">
              Escribiendo...
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white p-2">
        {sendError && (
          <p className="text-xs text-red-600 px-1 pb-1">
            No se pudo enviar. Inténtalo de nuevo.
          </p>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            rows={1}
            maxLength={2000}
            placeholder="Escribe tu mensaje..."
            className="flex-1 resize-none px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent max-h-24"
          />
          <button
            onClick={() => void handleSend()}
            disabled={!text.trim() || sending}
            className="p-2.5 rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 transition-colors flex-shrink-0"
            aria-label="Enviar"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
